import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FALLBACK_IMG =
  "https://images.unsplash.com/photo-1594125311687-3b1b3eafa9f4?auto=format&fit=crop&w=900&q=70";

export default function Discovery() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/discovery`)
      .then((r) => setData(r.data))
      .catch(() => {});
  }, []);

  if (!data) return null;
  const { perfume, month, headline, story } = data;

  return (
    <>
      {/* Discovery card — sits at bottom of hero right column */}
      <motion.button
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.4, duration: 0.9 }}
        onClick={() => setOpen(true)}
        className="group w-full text-left border border-border bg-background/60 backdrop-blur-sm p-5 sm:p-6 flex items-center gap-4 sm:gap-5 hover:border-foreground/60 transition-colors"
        data-testid="discovery-card"
      >
        <div className="w-16 h-20 sm:w-20 sm:h-24 flex-shrink-0 border border-border overflow-hidden bg-muted">
          <img
            src={perfume.image_url || FALLBACK_IMG}
            alt={perfume.name}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.currentTarget.src = FALLBACK_IMG;
            }}
          />
        </div>
        <div className="flex-1 min-w-0">
          <div
            className="label-caps flex items-center gap-2"
            style={{ color: "hsl(var(--gold))" }}
          >
            <Sparkles size={11} strokeWidth={1.5} />
            Discovery · {month}
          </div>
          <div className="font-serif text-xl sm:text-2xl mt-1.5 text-foreground truncate">
            {perfume.name}
          </div>
          <div className="text-foreground/50 text-xs mt-0.5 truncate">
            {perfume.brand} — tap to read the story
          </div>
        </div>
      </motion.button>

      {/* Full-screen story modal */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-[200] bg-background/95 backdrop-blur-xl overflow-y-auto no-scrollbar"
            data-testid="discovery-modal"
          >
            <div className="min-h-screen px-6 sm:px-12 lg:px-20 py-10 lg:py-14">
              <div className="flex items-center justify-between border-b border-border pb-6">
                <span
                  className="label-caps"
                  style={{ color: "hsl(var(--gold))" }}
                >
                  Discovery · {month}
                </span>
                <button
                  onClick={() => setOpen(false)}
                  className="btn-sharp"
                  data-testid="discovery-close-btn"
                >
                  <X size={13} strokeWidth={1.5} />
                  Close
                </button>
              </div>

              <div className="pt-14 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                  className="lg:col-span-5"
                >
                  <div className="aspect-[3/4] border border-border overflow-hidden bg-muted">
                    <img
                      src={perfume.image_url || FALLBACK_IMG}
                      alt={perfume.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.src = FALLBACK_IMG;
                      }}
                    />
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 1, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
                  className="lg:col-span-7"
                >
                  <div className="label-caps text-foreground/60">
                    This month's pick
                  </div>
                  <h2 className="font-serif text-4xl sm:text-6xl lg:text-7xl mt-3 leading-[1] text-foreground">
                    {perfume.name}
                  </h2>
                  <div className="mt-3 label-caps text-foreground/70">
                    {perfume.brand}
                  </div>

                  <h3 className="font-serif italic text-2xl sm:text-3xl mt-10 text-foreground/90 leading-snug max-w-xl">
                    &ldquo;{headline}&rdquo;
                  </h3>

                  <p className="mt-8 text-foreground/80 leading-relaxed text-lg max-w-2xl">
                    {story}
                  </p>

                  <div className="mt-12 grid grid-cols-2 gap-x-8 gap-y-5 max-w-xl">
                    <div>
                      <div className="label-caps text-foreground/50">
                        Notes
                      </div>
                      <div className="mt-2 text-foreground/85 text-sm">
                        {perfume.notes.slice(0, 6).join(" · ")}
                      </div>
                    </div>
                    <div>
                      <div className="label-caps text-foreground/50">
                        Families
                      </div>
                      <div className="mt-2 text-foreground/85 text-sm">
                        {perfume.families.join(" · ")}
                      </div>
                    </div>
                    <div>
                      <div className="label-caps text-foreground/50">
                        Best for
                      </div>
                      <div className="mt-2 text-foreground/85 text-sm">
                        {perfume.occasions.slice(0, 3).join(" · ")}
                      </div>
                    </div>
                    <div>
                      <div className="label-caps text-foreground/50">
                        Price
                      </div>
                      <div className="mt-2 text-foreground/85 text-sm">
                        ${perfume.price_usd}
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
